import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { Toaster } from "@/components/ui/sonner";
import { NotificationDock } from "@/components/ui/NotificationDock";
import { ExecutiveDemoOverlay } from "@/components/demo/ExecutiveDemoOverlay";
import { ExecutiveDemoSeeder } from "@/components/demo/ExecutiveDemoSeeder";
import { RegistrationGate } from "@/components/onboarding/RegistrationGate";
import { UnlockExperienceModal } from "@/components/onboarding/UnlockExperienceModal";
import { GlobalErrorBoundary } from "@/components/system/GlobalErrorBoundary";
import { OfflineBanner } from "@/components/system/OfflineBanner";
import { useEffect, useRef } from "react";
import { startBrandSanitizer } from "@/lib/brandSanitizer";
import { initBatteryMode } from "@/lib/batteryMode";
import { loadThemePalette } from "@/lib/pr21/themePalettes";
import { ensureThemeBootstrapped } from "@/lib/pr35d/teamThemeEngine";
import { applyLiveTheme } from "@/lib/pr35d1/liveThemeApplication";
import { bootstrapNationThemeBridge } from "@/lib/identity/nationThemeBridge";
import { PostMatchSummaryModal } from "@/components/match/PostMatchSummaryModal";
import { PredictiveAlertToast } from "@/components/match/PredictiveAlertToast";
import { initViralCapture } from "@/lib/viralCapture";
import { attributePendingReferral } from "@/lib/viralCapture";
import { initAttributionEngine, attachUserToAttribution, trackAttributionEvent } from "@/lib/attribution/attributionEngine";
import { migrateGuestStateToProfile } from "@/lib/auth/authClient";
import { TenantProvider } from "@/lib/tenants/tenantContext";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { CountryProvider } from "@/lib/country/CountryContext";
import { initMobileMatchMode } from "@/lib/launchHardening/mobileMatchMode";
import { RealtimeStatusPill } from "@/components/system/RealtimeStatusPill";
import { SafeModeUI } from "@/components/system/SafeModeUI";
import { initNetworkHealth } from "@/lib/resilience/networkHealth";
import { StaleBuildBanner } from "@/components/system/StaleBuildBanner";
import { ShareSheetHost } from "@/components/viral/ShareSheetHost";
import { MobileShareDock } from "@/components/mobile/MobileShareDock";
import { MobileQuickReturnChip } from "@/components/mobile/MobileQuickReturnChip";
import { initQuickReturnListener } from "@/lib/mobile/quickReturn";
import { initDeployWatcher } from "@/lib/stability/deployVersion";
import { initChunkRecovery } from "@/lib/stability/chunkRecovery";
import { bootstrapRecoveryRestore } from "@/lib/stability/recoveryBoot";
import { runStartup } from "@/lib/stability/startupOrchestrator";
import { bootstrapActivationListener } from "@/lib/activation";
import { bootstrapFirst72Telemetry } from "@/lib/warRoom/firstHoursTelemetry";
import { bootstrapWarRoomCounters } from "@/lib/warRoom/eventCounters";
import { bootstrapConversionFunnel } from "@/lib/conversion/funnelEngine";
import { ConversionMomentsHost } from "@/components/conversion/ConversionMomentsHost";
import { initMarketEngine } from "@/lib/marketDomination/marketScoreEngine";
import { initBrasilMetrics } from "@/lib/growth/brasilStateMetrics";
import { initIndiaMetrics } from "@/lib/growth/indiaMetrics";
import { startMarketAlerts } from "@/lib/marketDomination/marketAlerts";
import { initSentry } from "@/lib/pr33/sentry";
import { initPostHog, identifyUser, resetPostHog, attachAuditBridge } from "@/lib/posthog";
import { IsolatedFlag, Isolated } from "@/components/system/IsolatedFlag";
import { lazy, Suspense } from "react";

// Volatile / non-critical surfaces are lazy-loaded so a heavy import failure
// can never block the critical shell, and they stay out of startup parse cost.
const SmartPushPrompt = lazy(() =>
  import("@/components/push/SmartPushPrompt").then((m) => ({ default: m.SmartPushPrompt })),
);

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover, maximum-scale=5" },
      { title: "VerseSport — Inteligencia Deportiva en Tiempo Real" },
      { name: "description", content: "Plataforma global de segunda pantalla con telemetría, IA, predicciones, recompensas y analítica comercial en tiempo real." },
      { name: "author", content: "Inneli Technology Corp. PBC" },
      { property: "og:title", content: "VerseSport — Inteligencia Deportiva en Tiempo Real" },
      { property: "og:description", content: "Plataforma global de segunda pantalla con telemetría, IA, predicciones, recompensas y analítica comercial en tiempo real." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "VerseSport — Inteligencia Deportiva en Tiempo Real" },
      { name: "twitter:description", content: "Plataforma global de segunda pantalla con telemetría, IA, predicciones, recompensas y analítica comercial en tiempo real." },
      { name: "theme-color", content: "#D50000" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "apple-mobile-web-app-title", content: "VerseSport" },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "manifest", href: "/manifest.webmanifest" },
      { rel: "apple-touch-icon", href: "/icon-512.png" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();

  useEffect(() => {
    void runStartup([
      { id: "sentry", run: () => initSentry() },
      { id: "posthog", run: () => { initPostHog(); attachAuditBridge(); } },
      { id: "recovery-restore", run: () => bootstrapRecoveryRestore() },
      { id: "brand-sanitizer", run: () => startBrandSanitizer() },
      { id: "battery-mode", run: () => initBatteryMode() },
      { id: "theme-palette", run: () => loadThemePalette() },
      { id: "team-theme", run: () => ensureThemeBootstrapped() },
      { id: "live-theme", run: () => applyLiveTheme() },
      { id: "nation-theme-bridge", run: () => bootstrapNationThemeBridge() },
      { id: "viral-capture", run: () => initViralCapture() },
      { id: "attribution-engine", run: () => initAttributionEngine() },
      { id: "mobile-match-mode", run: () => initMobileMatchMode() },
      { id: "network-health", run: () => initNetworkHealth() },
      { id: "deploy-watcher", run: () => initDeployWatcher() },
      { id: "chunk-recovery", run: () => initChunkRecovery() },
      { id: "activation-listener", run: () => bootstrapActivationListener() },
      { id: "warroom-counters", run: () => bootstrapWarRoomCounters() },
      { id: "warroom-first72", run: () => bootstrapFirst72Telemetry() },
      { id: "conversion-funnel", run: () => bootstrapConversionFunnel() },
      { id: "market-domination", run: () => { initMarketEngine(); startMarketAlerts(); } },
      { id: "brasil-metrics", run: () => initBrasilMetrics() },
      { id: "india-metrics", run: () => initIndiaMetrics() },
      { id: "mobile-quick-return", run: () => initQuickReturnListener() },
    ]);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AuthSideEffects />
        <TenantProvider>
         <CountryProvider>
          <GlobalErrorBoundary>
            <Outlet />
          </GlobalErrorBoundary>
      <OfflineBanner />
      <Toaster
        position="bottom-right"
        theme="dark"
        richColors
        mobileOffset={{ bottom: "84px" }}
        offset={{ bottom: "24px", right: "16px" }}
      />
      <NotificationDock />
      <Isolated name="ExecutiveDemoOverlay"><ExecutiveDemoOverlay /></Isolated>
      <Isolated name="ExecutiveDemoSeeder"><ExecutiveDemoSeeder /></Isolated>
      <Isolated name="RegistrationGate"><RegistrationGate /></Isolated>
      <Isolated name="UnlockExperienceModal"><UnlockExperienceModal /></Isolated>
      <IsolatedFlag name="PostMatchSummaryModal" flag="cinematic"><PostMatchSummaryModal /></IsolatedFlag>
      <IsolatedFlag name="PredictiveAlertToast" flag="cinematic"><PredictiveAlertToast /></IsolatedFlag>
      <Isolated name="RealtimeStatusPill"><RealtimeStatusPill /></Isolated>
      <Isolated name="SafeModeUI"><SafeModeUI /></Isolated>
      <IsolatedFlag name="SmartPushPrompt" flag="push">
        <Suspense fallback={null}><SmartPushPrompt /></Suspense>
      </IsolatedFlag>
      <Isolated name="StaleBuildBanner"><StaleBuildBanner /></Isolated>
      <Isolated name="ShareSheetHost"><ShareSheetHost /></Isolated>
      <Isolated name="ConversionMomentsHost"><ConversionMomentsHost /></Isolated>
      <Isolated name="MobileShareDock"><MobileShareDock /></Isolated>
      <Isolated name="MobileQuickReturnChip"><MobileQuickReturnChip /></Isolated>
         </CountryProvider>
        </TenantProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

/**
 * P0-AUTH-07 — Side-effects driven by the global AuthProvider. Previously
 * lived as a second `supabase.auth.onAuthStateChange` listener inside
 * RootComponent. Now it just observes the provider's `session`/`user`
 * transitions, so the app keeps a SINGLE Supabase auth subscription.
 */
function AuthSideEffects() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();
  const { session, user } = useAuth();
  const lastUidRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const uid = user?.id ?? null;
    if (lastUidRef.current === undefined) {
      // First observation — don't treat hydration as a SIGN_IN.
      lastUidRef.current = uid;
      return;
    }
    if (lastUidRef.current === uid) return;

    const wasSignedOut = lastUidRef.current === null;
    const nowSignedIn = !!uid;
    lastUidRef.current = uid;

    if (wasSignedOut && nowSignedIn && uid) {
      void attributePendingReferral();
      attachUserToAttribution(uid);
      trackAttributionEvent("signup", { provider: session?.user?.app_metadata?.provider }, uid);
      void migrateGuestStateToProfile();
      identifyUser(uid, {
        email: user?.email ?? undefined,
        provider: session?.user?.app_metadata?.provider,
      });
    }
    // On sign-out (was signed in, now signed out), clear PostHog identity.
    if (!wasSignedOut && !nowSignedIn) {
      resetPostHog();
    }

    router.invalidate();
    queryClient.invalidateQueries();
  }, [user, session, router, queryClient]);

  return null;
}
